import { Component, OnInit } from '@angular/core';
import { NotifierService } from 'angular-notifier';
import { AdvancedFilterService } from './advanced-filter.service';

@Component({
  selector: 'app-advanced-filter',
  templateUrl: './advanced-filter.component.html',
  styleUrls: ['./advanced-filter.component.scss']
})
export class AdvancedFilterComponent implements OnInit {
  // Track search input for each filter
  // filterSearchText: { [key: string]: string } = {};

  constructor(
    public aFilterService: AdvancedFilterService,
    private notifier: NotifierService
  ) { }

  ngOnInit(): void {
    // Initialize options backup and search text for each filter
    this.initializeFilterOptions();
  }

  /**
   * Initialize original options backup for all filters.
   * This allows us to restore the full list when clearing the search.
   */
  initializeFilterOptions(): void {
    this.aFilterService.advanceFilter.forEach(group => {
      (group.filters || []).forEach((filter: any) => {
        // Only backup options for filters with array-type options (skip date-range, from-to)
        const hasArrayOptions = filter.options && Array.isArray(filter.options);
        if (hasArrayOptions && !filter.optionsBackup) {
          // Store a backup of the original options
          filter.optionsBackup = JSON.parse(JSON.stringify(filter.options));
        }
        // Initialize search text
        if (filter.searchText === undefined) {
          filter.searchText = '';
        }
      });
    });
  }

  /**
   * Track fn for ngFor over filters (keeps DOM stable when values change).
   */
  trackByKey(_index: number, item: any) {
    return item && item.key;
  }

  /**
   * Filter options based on search text.
   * Searches in the 'name' property of each option.
   */
  onFilterSearchChange(filter: any, searchText: string): void {
    filter.searchText = searchText;
    // Only apply search to filters with array-type options
    if (!Array.isArray(filter.optionsBackup)) { return; }
    
    if (!searchText || searchText.trim() === '') {
      // Restore all options if search is empty
      filter.options = JSON.parse(JSON.stringify(filter.optionsBackup || []));

    } else {
      // Filter options based on search text (case-insensitive)
      const lowerSearchText = searchText.toLowerCase();
      filter.options = (filter.optionsBackup || []).filter((opt: any) =>
        opt.name && opt.name.toLowerCase().includes(lowerSearchText) || filter.selectedValue && filter.selectedValue.includes(opt.value)
      );
    }
  }

  /**
   * Handle multi-select changes and filter out the Select All marker value.
   */
  onMultiSelectChange(filter: any): void {
    if (!filter.selectedValue) {
      filter.selectedValue = [];
      return;
    }
    // Remove the __selectAll__ marker if it was accidentally added
    const selectAllIndex = filter.selectedValue.indexOf('__selectAll__');
    if (selectAllIndex > -1) {
      // filter.selectedValue.splice(selectAllIndex, 1);
    }
    if (filter && filter.dependent && filter.dependent.length > 0) {
      // console.log("Single select changed for filter: ", filter);
      let body = {
        "filter":{[filter.key]: filter.selectedValue},
        "dependent":filter.dependent
      };
      this.aFilterService.getDependentFilters(body);
    }
  }

    /**
   * Handle single-select changes and trigger dependent filters or on_change actions.
   */
  onSingleSelectChange(filter: any): void {
    if (filter && filter.dependent && filter.dependent.length > 0) {
      console.log("Single select changed for filter: ", filter);
      let body = {
        "filter":{[filter.key]: filter.selectedValue},
        "dependent":filter.dependent
      };
      this.aFilterService.getDependentFilters(body);
    }
    if(filter && filter.on_change && filter.on_change.length > 0) {
      filter.on_change.forEach(action=> {
        if (action && action.value === filter.selectedValue) {
          if(action.type === 'group') {
            this.updateFilterGroup(action);
          }else{
            this.updateFilter(action);
          }
        }
      });
    }
  }

  updateFilterGroup(action: any): void {
    // Implement the logic to update the filter group based on the action
    // console.log("Updating filter group with action: ", action);
    if (action && action.action) {
      let filterToUpdate = this.aFilterService.advanceFilter.find((grp: any) => grp.key === action.key);
      if (filterToUpdate) {
        // Merge all keys from action.action into filterToUpdate
        // Updates existing keys and adds new ones
        Object.assign(filterToUpdate, action.action);
      }
    }
  }
  
  updateFilter(action: any): void {
    // Implement the logic to update the filter based on the action
    // console.log("Updating filter with action: ", action);
  }


  /**
   * Toggle select all for multi-select filters.
   * Selects/deselects all available options based on the current state.
   */
  toggleSelectAll(filter: any): void {
    if (!filter.selectedValue) {
      filter.selectedValue = [];
    }

    const availableOptionsLength = filter.options ? filter.options.length : 0;
    const allValuesArray = filter.options.map((opt: any) => opt.value);

    // Check if all options are currently selected
    const allSelected = allValuesArray.length > 0 &&
      allValuesArray.every((val: any) => filter.selectedValue.includes(val));

    if (allSelected) {
      // Deselect all
      filter.selectedValue = [];
    } else {
      // Select all available options
      filter.selectedValue = [...allValuesArray];
    }

    // Clear the invalid flag when selection changes
    this.aFilterService.clearInvalid(filter);
  }

  /**
   * Check if all options are currently selected (for UI indication).
   */
  areAllSelected(filter: any): boolean {
    if (!filter.selectedValue || !filter.options) {
      return false;
    }
    return filter.selectedValue.length === filter.options.length && filter.options.length > 0;
  }

  submitFilter() {
    const { valid, message } = this.aFilterService.validate();
    if (!valid) {
      this.notifier.notify('error', message);
      return;
    }
    const payload = this.aFilterService.getPayload();
    if (!Object.keys(payload).length) {
      this.notifier.notify('warning', 'Please select at least one filter to apply.');
      return;
    }
    this.aFilterService.applyFilter();
  }
}
